import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, BookOpen, Newspaper, Home } from 'lucide-react';
import SEO from '../components/SEO';

const ThankYou = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <SEO 
        title="Thank You - Message Received"
        description="Thank you for contacting Laura Novak. Your message has been received and will be responded to within 24-48 hours."
        url="https://lauranovakwrites.com/thankyou"
      />
      
      <div className="max-w-4xl mx-auto px-6 py-20">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          {/* Main Message */}
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-4">
            Thank You!
          </h1>
          <p className="text-xl text-slate-600 mb-6">
            Your message has been sent successfully.
          </p>
        </div>

        {/* Featured Quote */}
        <div className="mb-10 text-center py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-4">
              <svg className="w-12 h-12 text-amber-500 mx-auto opacity-50" fill="currentColor" viewBox="0 0 32 32">
                <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-2.2 1.8-4 4-4V8zm16 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8z"/>
              </svg>
            </div>
            <blockquote className="text-2xl md:text-3xl font-serif text-slate-700 italic leading-relaxed mb-4">
              "The best story in the room is obvious if you stop talking and listen carefully."
            </blockquote>
            <p className="text-slate-500 text-lg font-medium">— Laura Novak</p>
          </div>
        </div>

        {/* What to Expect Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            What Happens Next?
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mb-4">
            I've received your message and I'm excited to read it! I typically respond to all inquiries within <span className="font-semibold text-amber-600">24-48 hours</span>.
          </p>
          <p className="text-slate-600">
            If you don't hear from me within this timeframe, please check your spam folder or feel free to reach out directly at{' '}
            <a href="mailto:laura.a.novak@gmail.com" className="text-amber-600 hover:text-amber-700 underline">
              Laura Novak
            </a>
          </p>
        </div>

        {/* While You Wait Section */}
        <div className="bg-slate-900 rounded-2xl p-8 mb-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-3">
            While You Wait ...
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Explore more of my work and latest updates
          </p>

          <div className="grid md:grid-cols-2 gap-4 max-w-xl mx-auto">
            <Link to="/books">
              <Button 
                size="lg" 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Explore My Books
              </Button>
            </Link>
            
            <Link to="/news">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 py-6 text-lg"
              >
                <Newspaper className="w-5 h-5 mr-2" />
                Read Latest News
              </Button>
            </Link>
          </div>
        </div>

        {/* Return Home */}
        <div className="text-center">
          <Link to="/">
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              <Home className="w-5 h-5 mr-2" />
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
